import { Component, OnInit, inject } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { differenceInDays } from 'date-fns';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ReconciliationVariateService } from '../../../services/reconciliation-variate.service';
import { FuelKpiRequest, GroupedBarChartComponent, FuelInsightsRequest } from "../../../../_shared";
import { FuelInsightsResponse } from "../../../../_shared/models/fuel-insights-response.model";
import { formatCost, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { DropdownOption } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { GraphTrendIconComponent } from "../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component";
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-reconciliation-variant-widget-graph',
  standalone: true,
  imports: [GroupedBarChartComponent, CommonModule, GraphTrendIconComponent, EmptyStateComponent, TranslocoDirective],
  templateUrl: './reconciliation-variant-widget-graph.component.html',
  styleUrl: './reconciliation-variant-widget-graph.component.scss'
})

export class ReconciliationVariantWidgetGraphComponent extends BaseWidgetComponent implements OnInit {
  private transloco = inject(TranslocoService);

  private readonly defaultYTicks = [60, 50, 40, 30, 20, 10, 0];

  protected yTicks: number[] = this.defaultYTicks;
  hasError: boolean = false;
  errorMessage: string = '';
  currencyCode: string = '';
  currencySymbol: string = '';
  override isLoading: boolean = true;
  selectedUnit = MeasurementTypes.Volume.toString();
  chartData: any[];

  dropdownOptions: DropdownOption[] = [
    { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
    { label: this.transloco.translate('fuelDashboard.units.rands'), value: MeasurementTypes.CostPerUnit.toString(), unit: 'R' }
  ];

  constructor(
    filterService: FilterService,
    private reconcilationService: ReconciliationVariateService,
    private errorHandlerService: ErrorHandlerService,
    private auth: AuthService
  ) {
    super(filterService);
  }

  ngOnInit(): void {
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.dropdownOptions = [
      { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.cost', { currency: this.currencyCode }), value: MeasurementTypes.CostPerUnit.toString(), unit: '' },
    ];
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.getReconWidgetGraphData();
  }

  formatPercentChange(): string {
    if (this.percentageDifference != null) {
      const sign = this.percentageDifference > 0 ? '+' : '';
      return `${sign}${this.percentageDifference.toFixed(2)}%`;
    }

    return this.transloco.translate('fuelShared.charts.notAvailable');
  }

  formatValue(value: number): string {
    return this.getTooltipUnit() == this.currencySymbol
      ? formatCost(value, this.currencySymbol)
      : formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  getTooltipUnit(): string {
    const selectedOption = this.dropdownOptions.find(opt => opt.value === this.selectedUnit);
    if (this.selectedUnit === MeasurementTypes.CostPerUnit.toString()) {
      return this.currencySymbol;
    }
    return selectedOption?.unit || 'ℓ';
  }

  getArrowIcon(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0
        ? 'assets/images-fuel/yellowArrowIcon.svg'
        : 'assets/images-fuel/greenArrowIcon.svg';
    }

    return 'assets/images-fuel/yellowArrowIcon.svg';
  }

  getPercentChangeClass(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0 ? 'percent-increase' : 'percent-decrease';
    }

    return '';
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) {
      return false;
    }

    return this.chartData.some(item => {
      const boughtValue = Number(item['Bought']) || 0;
      const detectedValue = Number(item['Detected']) || 0;
      return boughtValue > 0 || detectedValue > 0;
    });
  }

  async getReconcilationSummary() {
    try {
      const request: FuelKpiRequest = {
        fuelMeasurementType: getEnumFromString(this.selectedUnit),
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!

      };

      this.cancelSubject.next();
      var summary = await this.reconcilationService.getReconciliationVariateSummary(request, this.cancelSubject)
      if (summary.data) {
        this.percentageDifference = summary.data.percentageChange;
        this.value = summary.data.totalValue
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  private async getReconWidgetGraphData(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const request: FuelInsightsRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        fuelMeasurementType: getEnumFromString(this.selectedUnit),
        interval: this.widgetFilter.interval
      };

      const isVolume = this.selectedUnit === MeasurementTypes.Volume.toString();

      const [_, fuelBoughtResponse, fuelDetectedResponse] = await Promise.all([
        this.getReconcilationSummary(),
        isVolume
          ? this.reconcilationService.getFuelBoughtInsights(request, this.cancelSubject)
          : this.reconcilationService.getFuelBoughtCostInsights(request, this.cancelSubject),
        isVolume
          ? this.reconcilationService.getFuelDetectedInsights(request, this.cancelSubject)
          : this.reconcilationService.getFuelDetectedCostInsights(request, this.cancelSubject)
      ]);

      if (fuelBoughtResponse.data && fuelDetectedResponse.data) {
        const boughtArray = Array.isArray(fuelBoughtResponse.data)
          ? fuelBoughtResponse.data
          : [fuelBoughtResponse.data];
        const detectedArray = Array.isArray(fuelDetectedResponse.data)
          ? fuelDetectedResponse.data
          : [fuelDetectedResponse.data];

        this.chartData = this.processChartData(boughtArray, detectedArray);
        this.updateYTicks();
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    this.isLoading = true;
    await this.onFilterChangeAsync();
  }

  fullChartClick(): void {
    // TODO: Implement modal for full chart view
  }

  private processChartData(fuelBoughtData: FuelInsightsResponse[], fuelDetectedData: FuelInsightsResponse[]): any[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference);

    const datasets = [
      { data: fuelBoughtData, label: 'Bought' },
      { data: fuelDetectedData, label: 'Detected' }
    ];

    switch (groupingType) {
      case 'hourly':
        return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily':
        return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
      case 'weekly':
        return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
      default:
        return [];
    }
  }

  private determineGroupingType(daysDifference: number): 'hourly' | 'daily' | 'weekly' {
    // Group by day difference - covers all cases
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private updateYTicks(): void {
    if (!this.chartData || this.chartData.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const maxValue = Math.max(
      ...this.chartData.flatMap(item => [item.Bought || 0, item.Detected || 0])
    );

    if (maxValue === 0) {
      this.yTicks = [0];
      return;
    }

    const tickCount = 6;
    const step = Math.ceil(maxValue / tickCount);
    const roundedMax = step * tickCount;

    this.yTicks = [];
    for (let i = roundedMax; i >= 0; i -= step) {
      this.yTicks.push(i);
    }

    // Force a new array reference to trigger change detection
    this.yTicks = [...this.yTicks];
  }
}